import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Crown, Star, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SubscriptionSuccess = () => {
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [sessionDetails, setSessionDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Only add Authorization if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/checkout/status/${sessionId}`, {
        headers
      });

      if (!response.ok) {
        // If 401, user needs to log in
        if (response.status === 401) {
          setPaymentStatus('needs_login');
          return;
        }
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setSessionDetails(data);

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      // If payment is still pending, continue polling
      setPollAttempts(attempts + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = getUrlParameter('session_id');
    if (!sessionId) {
      setPaymentStatus('error');
      return;
    }

    try {
      // Try to activate subscription via backend (without auth)
      // This will update the database even if user isn't logged in yet
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscription/activate/${sessionId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        console.log('✅ Subscription activated successfully');
      } else {
        console.warn('⚠️ Subscription activation returned non-OK status:', response.status);
      }
    } catch (error) {
      console.warn('⚠️ Subscription activation call failed:', error);
      // Continue anyway - user can still log in
    }

    // Show success message - user needs to log in
    setPaymentStatus('needs_login');
  };

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case 'success':
      case 'needs_login':
        return <Check className="w-16 h-16 text-green-500" />;
      case 'checking':
        return <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />;
      case 'error':
      case 'timeout':
      case 'expired':
        return <AlertCircle className="w-16 h-16 text-red-500" />;
      default:
        return <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />;
    }
  };

  const getStatusMessage = () => {
    switch (paymentStatus) {
      case 'success':
        return {
          title: 'Payment Successful!',
          description: 'Welcome to TribalRoot.AI! Your subscription has been activated and you now have access to all premium features.',
          buttonText: 'Access Your Account',
          buttonLink: '/vault'
        };
      case 'needs_login':
        return {
          title: 'Registration Complete! 🎉',
          description: 'Your payment was successful and your subscription is now active! Click the button below to login with your email and password to access your premium membership.',
          buttonText: 'Login Now',
          buttonLink: '/'
        };
      case 'checking':
        return {
          title: 'Processing Payment...',
          description: `We're verifying your payment with our secure payment processor. This usually takes just a few seconds. (Attempt ${pollAttempts + 1}/8)`,
          buttonText: null,
          buttonLink: null
        };
      case 'timeout':
        return {
          title: 'Payment Verification Timeout',
          description: 'We\'re still processing your payment. Please check your email for confirmation or contact support if you don\'t receive an update within 10 minutes.',
          buttonText: 'Check Again',
          buttonLink: null
        };
      case 'expired':
        return {
          title: 'Payment Session Expired',
          description: 'Your payment session has expired. Please try subscribing again.',
          buttonText: 'Try Again',
          buttonLink: '/#pricing'
        };
      case 'error':
      default:
        return {
          title: 'Payment Verification Error',
          description: 'We encountered an error while verifying your payment. Please contact support or try again.',
          buttonText: 'Try Again',
          buttonLink: '/#pricing'
        };
    }
  };

  const statusInfo = getStatusMessage();

  const getTierIcon = () => {
    if (sessionDetails?.metadata?.subscription_tier === 'crown') {
      return <Crown className="w-8 h-8 text-amber-500" />;
    }
    return <Star className="w-8 h-8 text-blue-500" />;
  };

  const getTierName = () => {
    if (sessionDetails?.metadata?.subscription_tier === 'crown') {
      return 'Crown Subscription';
    }
    return 'Seekers Path';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-green-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full bg-white shadow-xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
            {statusInfo.title}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="text-center space-y-6">
          <p className="text-gray-600 leading-relaxed">
            {statusInfo.description}
          </p>

          {paymentStatus === 'success' && sessionDetails && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg">
              <div className="flex items-center justify-center mb-4">
                {getTierIcon()}
                <span className="ml-2 text-lg font-semibold text-gray-900">
                  {getTierName()}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Amount Paid:</span>
                  <div className="text-lg font-bold text-green-600">
                    ${(sessionDetails.amount_total / 100).toFixed(2)} {sessionDetails.currency?.toUpperCase()}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Payment Status:</span>
                  <div className="text-lg font-bold text-green-600 capitalize">
                    {sessionDetails.payment_status}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {statusInfo.buttonText && (
              <Button 
                size="lg"
                className="bg-green-700 hover:bg-green-800 text-white px-8 py-3"
                onClick={() => {
                  if (statusInfo.buttonLink) {
                    window.location.href = statusInfo.buttonLink;
                  } else {
                    checkPaymentStatus();
                  }
                }}
              >
                {statusInfo.buttonText}
              </Button>
            )}
            
            {paymentStatus !== 'checking' && (
              <Button 
                variant="outline"
                size="lg"
                className="px-8 py-3"
                onClick={() => window.location.href = '/'}
              >
                Return Home
              </Button>
            )}
          </div>

          {paymentStatus === 'success' && (
            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">What's Next?</h4>
              <ul className="text-sm text-blue-800 space-y-1 text-left">
                <li>• Check your email for a confirmation receipt</li>
                <li>• Access all premium features in your Tribal Vault</li>
                <li>• Explore your expanded ancestral heritage tools</li>
                <li>• Join our community of Indigenous sovereignty seekers</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;